import { useState, useEffect, useMemo, useCallback } from "react";
import { RefreshCw, Activity, TrendingUp, BarChart2, AlertCircle, LogOut } from "lucide-react";
import type { StockData } from "./api";
import { fetchStocks } from "./api";
import StockCard from "./components/StockCard";
import ScreenerPage from "./components/ScreenerPage";
import TradesPage from "./components/TradesPage";
import PerformancePage from "./components/PerformancePage";
import StockPicksPage from "./components/StockPicksPage";
import { AuthProvider, useAuth } from "./AuthContext";

// ─── Design tokens ─────────────────────────────────────────────────────────

const SECTORS = ["All", "Technology", "Finance", "Healthcare", "Energy", "Consumer", "Automotive"];
type SortKey = "iv" | "ivRank" | "change" | "ticker";
type Tab = "stock-picks" | "options" | "trades" | "performance";

// ─── Hash routing helpers ─────────────────────────────────────────────────

const VALID_TABS: Tab[] = ["stock-picks", "options", "trades", "performance"];
const DEFAULT_TAB: Tab = "stock-picks";

function parseHash(): { tab: Tab; subTab: string | null } {
  const hash = window.location.hash.replace(/^#\/?/, "");
  if (!hash) return { tab: DEFAULT_TAB, subTab: null };
  const [first, ...rest] = hash.split("/");
  const tab = VALID_TABS.includes(first as Tab) ? (first as Tab) : DEFAULT_TAB;
  const subTab = rest.length > 0 ? rest.join("/") : null;
  return { tab, subTab };
}

function setHash(tab: Tab, subTab?: string | null) {
  const path = subTab ? `${tab}/${subTab}` : tab;
  if (window.location.hash !== `#${path}`) {
    window.location.hash = path;
  }
}

// ─── MarketPage (original App content) ────────────────────────────────────

function MarketPage() {
  const [stocks, setStocks] = useState<StockData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [sector, setSector] = useState("All");
  const [sortBy, setSortBy] = useState<SortKey>("iv");
  const [minIV, setMinIV] = useState(0);

  async function loadStocks() {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchStocks();
      setStocks(data);
      setLastUpdated(new Date());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load stocks");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadStocks(); }, []);

  const filtered = useMemo(() => {
    return stocks
      .filter((s) => sector === "All" || s.sector.includes(sector))
      .filter((s) => s.iv >= minIV)
      .sort((a, b) => {
        if (sortBy === "iv") return b.iv - a.iv;
        if (sortBy === "ivRank") return b.ivRank - a.ivRank;
        if (sortBy === "change") return Math.abs(b.changePercent) - Math.abs(a.changePercent);
        return a.ticker.localeCompare(b.ticker);
      });
  }, [stocks, sector, sortBy, minIV]);

  const stats = useMemo(() => ({
    avgIV: stocks.length ? (stocks.reduce((a, s) => a + s.iv, 0) / stocks.length).toFixed(1) : "—",
    highIV: stocks.filter((s) => s.iv >= 30).length,
    highIVRank: stocks.filter((s) => s.ivRank >= 60).length,
    richPremium: stocks.filter((s) => s.hvRatio >= 1.3).length,
  }), [stocks]);

  return (
    <main style={{ maxWidth: 1440, margin: "0 auto", padding: "32px 40px 60px" }}>

      {/* ── Stats Bar ── */}
      {!loading && stocks.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 28 }}>
          {[
            { icon: <Activity size={16} />, label: "AVG IV", value: `${stats.avgIV}%`, color: "#60a5fa" },
            { icon: <BarChart2 size={16} />, label: "HIGH IV (≥30%)", value: stats.highIV, color: "#f87171" },
            { icon: <TrendingUp size={16} />, label: "HIGH IV RANK (≥60)", value: stats.highIVRank, color: "#facc15" },
            { icon: <TrendingUp size={16} />, label: "RICH PREMIUM (IV/HV≥1.3x)", value: stats.richPremium, color: "#4ade80" },
          ].map((s) => (
            <div key={s.label} style={{
              background: "#0a1628", border: "1px solid #1e293b", borderRadius: 12, padding: "16px 20px",
              display: "flex", alignItems: "center", gap: 12,
            }}>
              <div style={{ color: s.color }}>{s.icon}</div>
              <div>
                <div style={{ fontSize: 9, color: "#475569", letterSpacing: 1, marginBottom: 3, fontFamily: "monospace" }}>{s.label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: s.color, fontFamily: "'Space Mono', monospace" }}>{s.value}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Filters ── */}
      <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {SECTORS.map((s) => (
            <button key={s} onClick={() => setSector(s)} style={{
              padding: "6px 14px", borderRadius: 20, border: "1px solid",
              borderColor: sector === s ? "#1d4ed8" : "#1e293b",
              background: sector === s ? "#1d4ed820" : "transparent",
              color: sector === s ? "#60a5fa" : "#475569",
              fontSize: 11, cursor: "pointer", fontFamily: "monospace",
            }}>{s.toUpperCase()}</button>
          ))}
        </div>

        <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
          <span style={{ fontSize: 10, color: "#475569", fontFamily: "monospace" }}>MIN IV:</span>
          <input type="range" min={0} max={50} step={5} value={minIV}
            onChange={(e) => setMinIV(parseInt(e.target.value))}
            style={{ width: 100, accentColor: "#1d4ed8" }} />
          <span style={{ fontSize: 11, color: "#94a3b8", fontFamily: "monospace", width: 32 }}>{minIV}%</span>

          <span style={{ fontSize: 10, color: "#475569", fontFamily: "monospace", marginLeft: 8 }}>SORT:</span>
          {(["iv", "ivRank", "change", "ticker"] as SortKey[]).map((k) => (
            <button key={k} onClick={() => setSortBy(k)} style={{
              padding: "5px 10px", borderRadius: 6, border: "none",
              background: sortBy === k ? "#1e293b" : "transparent",
              color: sortBy === k ? "#94a3b8" : "#334155",
              fontSize: 10, cursor: "pointer", fontFamily: "monospace",
            }}>{k.toUpperCase()}</button>
          ))}
        </div>
      </div>

      {/* ── Refresh row ── */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16, gap: 12, alignItems: "center" }}>
        {lastUpdated && (
          <span style={{ fontSize: 11, color: "#334155", fontFamily: "monospace" }}>
            Updated {lastUpdated.toLocaleTimeString()}
          </span>
        )}
        <button onClick={loadStocks} disabled={loading} style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "8px 16px", borderRadius: 8, border: "1px solid #1e293b",
          background: "#0d1526", color: "#64748b", cursor: loading ? "default" : "pointer",
          fontSize: 12, fontFamily: "monospace",
        }}>
          <RefreshCw size={13} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
          {loading ? "LOADING..." : "REFRESH"}
        </button>
      </div>

      {/* ── Error ── */}
      {error && (
        <div style={{
          background: "#1a0a0a", border: "1px solid #7f1d1d", borderRadius: 12,
          padding: 20, marginBottom: 24, display: "flex", gap: 12, alignItems: "flex-start",
        }}>
          <AlertCircle size={18} color="#f87171" style={{ flexShrink: 0 }} />
          <div>
            <div style={{ color: "#f87171", fontWeight: 600, marginBottom: 4 }}>Failed to load market data</div>
            <div style={{ color: "#7f1d1d", fontSize: 12, fontFamily: "monospace" }}>{error}</div>
            <div style={{ color: "#64748b", fontSize: 12, marginTop: 8 }}>
              Check that your <code style={{ color: "#94a3b8" }}>POLYGON_API_KEY</code> secret is set in the Worker.
            </div>
          </div>
        </div>
      )}

      {/* ── Loading skeleton ── */}
      {loading && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 20 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={{
              height: 240, background: "#0a1628", borderRadius: 16,
              border: "1px solid #1e293b", animation: "pulse 1.5s ease infinite",
              opacity: 1 - i * 0.1,
            }} />
          ))}
        </div>
      )}

      {/* ── Stock Grid ── */}
      {!loading && filtered.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))", gap: 20 }}>
          {filtered.map((stock) => (
            <StockCard key={stock.ticker} stock={stock} />
          ))}
        </div>
      )}

      {!loading && filtered.length === 0 && !error && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#334155" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>◈</div>
          <div style={{ fontFamily: "monospace" }}>No stocks match your filters</div>
        </div>
      )}

      {/* ── Disclaimer ── */}
      <div style={{
        marginTop: 48, padding: 20, background: "#0a1628",
        border: "1px solid #1e293b", borderRadius: 12, fontSize: 11,
        color: "#334155", lineHeight: 1.8, textAlign: "center",
      }}>
        <strong style={{ color: "#475569" }}>For educational purposes only.</strong>{" "}
        Options involve significant risk. Always verify data independently.
        Not financial advice. Consult a licensed advisor before trading.
      </div>
    </main>
  );
}

// ─── Login Page ─────────────────────────────────────────────────────────────

function LoginPage() {
  const { login } = useAuth();

  return (
    <div style={{
      minHeight: "100vh",
      background: "#050c18",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    }}>
      <div style={{
        background: "#0a1628",
        border: "1px solid #1e293b",
        borderRadius: 20,
        padding: "48px 56px",
        textAlign: "center",
        maxWidth: 420,
        width: "100%",
      }}>
        <img src="/logo.svg" alt="The Agentic Trader" style={{ width: 180, margin: "0 auto 20px", display: "block" }} />
        <p style={{ color: "#475569", fontSize: 14, margin: "0 0 36px", lineHeight: 1.6 }}>
          AI-powered options & stock portfolio management
        </p>

        <button onClick={login} style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
          width: "100%", padding: "14px 24px",
          borderRadius: 12, border: "1px solid #1e293b",
          background: "#0d1526", color: "#e2e8f0",
          fontSize: 15, fontWeight: 600, cursor: "pointer",
          fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
          transition: "all 0.15s ease",
        }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#1d4ed8'; e.currentTarget.style.background = '#1d4ed810'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#1e293b'; e.currentTarget.style.background = '#0d1526'; }}
        >
          <svg width="20" height="20" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Sign in with Google
        </button>

        <p style={{ color: "#334155", fontSize: 11, marginTop: 24, fontFamily: "monospace" }}>
          Your trades and portfolio are private to your account
        </p>
      </div>
    </div>
  );
}

// ─── Header ────────────────────────────────────────────────────────────────

const NAV_GROUPS: { label: string; accent: string; accentBg: string; tabs: { key: Tab; label: string; icon: React.ReactNode }[] }[] = [
  {
    label: "Trade",
    accent: "#60a5fa",
    accentBg: "#1d4ed8",
    tabs: [
      { key: "stock-picks", label: "Stock Picks", icon: <TrendingUp size={14} /> },
      { key: "options", label: "Options", icon: <Activity size={14} /> },
    ],
  },
  {
    label: "Portfolio",
    accent: "#4ade80",
    accentBg: "#16a34a",
    tabs: [
      { key: "trades", label: "Trades", icon: <BarChart2 size={14} /> },
      { key: "performance", label: "Performance", icon: <TrendingUp size={14} /> },
    ],
  },
];

function Header({ tab, setTab }: { tab: Tab; setTab: (t: Tab) => void }) {
  const { user, logout } = useAuth();

  // Find which group the current tab belongs to
  const activeGroup = NAV_GROUPS.find(g => g.tabs.some(t => t.key === tab));

  return (
    <header style={{
      background: "linear-gradient(180deg, #09122090 0%, #050c1800 100%)",
      borderBottom: "1px solid #1e293b",
      padding: "0 40px",
      position: "sticky", top: 0, zIndex: 100,
      backdropFilter: "blur(24px)",
    }}>
      <div style={{
        maxWidth: 1440, margin: "0 auto",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        height: 64,
      }}>
        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img src="/favicon.svg" alt="" style={{ width: 28, height: 28 }} />
          <h1 style={{
            margin: 0, fontSize: 20, fontWeight: 800,
            fontFamily: "'Space Mono', monospace",
            background: "linear-gradient(135deg, #f1f5f9 0%, #475569 100%)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          }}>
            The Agentic Trader
          </h1>
        </div>

        {/* Grouped Nav */}
        <nav style={{ display: "flex", alignItems: "center", gap: 0 }}>
          {NAV_GROUPS.map((group, gi) => (
            <div key={group.label} style={{ display: "flex", alignItems: "center" }}>
              {/* Divider between groups */}
              {gi > 0 && (
                <div style={{
                  width: 1, height: 28,
                  background: "linear-gradient(180deg, transparent, #1e293b, transparent)",
                  margin: "0 16px",
                }} />
              )}

              {/* Group container */}
              <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                {/* Group label */}
                <span style={{
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  textTransform: "uppercase" as const,
                  color: activeGroup === group ? group.accent : "#334155",
                  fontFamily: "'Space Mono', monospace",
                  marginRight: 10,
                  transition: "color 0.2s ease",
                  userSelect: "none" as const,
                  whiteSpace: "nowrap" as const,
                }}>
                  {group.label}
                </span>

                {/* Tab buttons */}
                {group.tabs.map(({ key, label, icon }) => {
                  const isActive = tab === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setTab(key)}
                      style={{
                        position: "relative" as const,
                        padding: "6px 16px",
                        borderRadius: 8,
                        border: "none",
                        background: isActive ? `${group.accentBg}18` : "transparent",
                        color: isActive ? group.accent : "#64748b",
                        fontSize: 13,
                        fontWeight: isActive ? 600 : 400,
                        cursor: "pointer",
                        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                        transition: "all 0.15s ease",
                        letterSpacing: 0.2,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <span style={{
                        opacity: isActive ? 1 : 0.6,
                        transition: "opacity 0.15s ease",
                        display: "flex",
                      }}>
                        {icon}
                      </span>
                      {label}
                      {/* Active indicator line */}
                      {isActive && (
                        <span style={{
                          position: "absolute" as const,
                          bottom: -19,
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: "60%",
                          height: 2,
                          background: `linear-gradient(90deg, transparent, ${group.accent}, transparent)`,
                          borderRadius: 1,
                        }} />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* User avatar + logout */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {user && (
            <>
              {user.picture ? (
                <img
                  src={user.picture}
                  alt={user.name ?? user.email}
                  style={{
                    width: 32, height: 32, borderRadius: "50%",
                    border: "2px solid #1e293b",
                  }}
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div style={{
                  width: 32, height: 32, borderRadius: "50%",
                  background: "#1e293b", display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#94a3b8", fontSize: 14, fontWeight: 700,
                }}>
                  {(user.name ?? user.email)[0].toUpperCase()}
                </div>
              )}
              <button
                onClick={logout}
                title="Sign out"
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: "#475569", padding: 4, display: "flex",
                }}
              >
                <LogOut size={16} />
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

// ─── GlobalStyles ──────────────────────────────────────────────────────────

function GlobalStyles() {
  return (
    <style>{`
      @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      @keyframes pulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 0.7; } }
      @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes slideUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { background: #050c18; }
      ::-webkit-scrollbar { width: 6px; }
      ::-webkit-scrollbar-track { background: #0a1628; }
      ::-webkit-scrollbar-thumb { background: #1e293b; border-radius: 3px; }
      button:focus-visible { outline: 2px solid #1d4ed8; outline-offset: 2px; }
      input:focus { outline: 1px solid #1d4ed8; }
    `}</style>
  );
}

// ─── App Shell (auth-gated) ─────────────────────────────────────────────────

function AppShell() {
  const { user, loading } = useAuth();
  const [tab, setTabState] = useState<Tab>(() => parseHash().tab);
  const [subTab, setSubTabState] = useState<string | null>(() => parseHash().subTab);

  const setTab = useCallback((t: Tab) => {
    setTabState(t);
    setSubTabState(null);
    setHash(t);
  }, []);

  const setSubTab = useCallback((s: string | null) => {
    setSubTabState(s);
    setHash(tab, s);
  }, [tab]);

  // Listen for back/forward navigation
  useEffect(() => {
    function onHashChange() {
      const { tab: newTab, subTab: newSub } = parseHash();
      setTabState(newTab);
      setSubTabState(newSub);
    }
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  // Set default hash if none present
  useEffect(() => {
    if (!window.location.hash) {
      setHash(DEFAULT_TAB);
    }
  }, []);

  if (loading) {
    return (
      <div style={{
        minHeight: "100vh", background: "#050c18",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <div style={{
          width: 40, height: 40, border: "3px solid #1e293b",
          borderTopColor: "#60a5fa", borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
        }} />
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  return (
    <div style={{
      minHeight: "100vh",
      background: "#050c18",
      color: "#e2e8f0",
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    }}>
      <Header tab={tab} setTab={setTab} />
      {tab === "stock-picks" && <StockPicksPage subTab={subTab} onSubTabChange={setSubTab} />}
      {tab === "options" && <ScreenerPage />}
      {tab === "trades" && <TradesPage subTab={subTab} onSubTabChange={setSubTab} />}
      {tab === "performance" && <PerformancePage subTab={subTab} onSubTabChange={setSubTab} />}
    </div>
  );
}

// ─── App ───────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <AuthProvider>
      <link href="https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=DM+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap" rel="stylesheet" />
      <AppShell />
      <GlobalStyles />
    </AuthProvider>
  );
}
